import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import JSZip from "jszip";
import {
  ArrowLeft, Loader2, Music, User, Calendar, MessageSquare,
  Upload, Download, CheckCircle, Image as ImageIcon, FileText, Trash2, MoreHorizontal, RefreshCw,
  Play, Pause, Repeat
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { Tables } from "@/integrations/supabase/types";
import type { Database } from "@/integrations/supabase/types";

type SongOrder = Tables<"song_orders">;
type OrderStatus = Database["public"]["Enums"]["order_status"];

const statusOptions: { value: OrderStatus; label: string }[] = [
  { value: "submitted", label: "Submitted" },
  { value: "in_review", label: "In Review" },
  { value: "drafting_lyrics", label: "Drafting Lyrics" },
  { value: "composing", label: "Composing" },
  { value: "ready_for_review", label: "Ready for Review" },
  { value: "revision_requested", label: "Changes Requested" },
  { value: "approved", label: "Approved" },
  { value: "completed", label: "Completed" },
];

const eventTypeLabels: Record<string, string> = {
  wedding: "Wedding", birthday: "Birthday", anniversary: "Anniversary",
  graduation: "Graduation", memorial: "Memorial", proposal: "Proposal",
  baby_shower: "Baby Shower", retirement: "Retirement", other: "Other",
};

const styleLabels: Record<string, string> = {
  pop: "Pop", acoustic: "Acoustic", classical: "Classical", jazz: "Jazz",
  country: "Country", rnb: "R&B", rock: "Rock", folk: "Folk", electronic: "Electronic",
};

const customerPhotosPathPrefixes = [
  "/storage/v1/object/sign/customer-photos/",
  "/storage/v1/object/authenticated/customer-photos/",
  "/storage/v1/object/private/customer-photos/",
  "/storage/v1/object/public/customer-photos/",
  "/object/sign/customer-photos/",
  "/object/authenticated/customer-photos/",
  "/object/private/customer-photos/",
  "/object/public/customer-photos/",
];

const normalizeCustomerPhotosPath = (value: string): string | null => {
  let filePath = value.trim();

  for (const prefix of customerPhotosPathPrefixes) {
    if (filePath.includes(prefix)) {
      filePath = filePath.split(prefix)[1]?.split("?")[0]?.split("#")[0] || "";
      break;
    }
  }

  filePath = filePath.split("?")[0]?.split("#")[0] || filePath;
  if (filePath.startsWith("/")) filePath = filePath.slice(1);
  if (filePath.startsWith("customer-photos/")) {
    filePath = filePath.slice("customer-photos/".length);
  }

  if (!filePath || filePath.startsWith("http")) return null;

  try {
    filePath = decodeURIComponent(filePath);
  } catch {
    // keep original filePath if decode fails
  }

  return filePath || null;
};

const exportSurvey = (order: SongOrder, customerName?: string | null, customerEmail?: string | null) => {
  const lines = [
    `ORDER #${order.order_number}`,
    `Customer Name: ${customerName || "N/A"}`,
    `Customer Email: ${customerEmail || "N/A"}`,
    `Plan: ${order.plan_type}`,
    `Price Paid: ${order.price_paid ? `$${Number(order.price_paid).toFixed(2)}` : "N/A"}`,
    `Status: ${order.status}`,
    `Created: ${new Date(order.created_at).toLocaleString()}`,
    `Rush Delivery: ${order.rush_delivery ? "Yes" : "No"}`,
    "",
    "--- SURVEY DETAILS ---",
    `Occasion: ${eventTypeLabels[order.event_type] || order.event_type}`,
    `Event Date: ${order.event_date ? new Date(order.event_date).toLocaleDateString() : "N/A"}`,
    `Recipient Names: ${order.recipient_names}`,
    `Relationship: ${order.relationship_to_recipient || "N/A"}`,
    `Language: ${order.language || "English"}`,
    `Singer Gender: ${order.singer_gender || "N/A"}`,
    `Musical Style: ${order.musical_style}`,
    `Mood/Feeling: ${order.mood}`,
    `Tempo: ${order.tempo || "Medium"}`,
    "",
    "--- STORY & MEMORIES ---",
    order.memories,
    "",
    ...(order.specific_phrases ? ["--- SPECIFIC PHRASES ---", order.specific_phrases, ""] : []),
    ...(order.additional_notes ? ["--- ADDITIONAL NOTES ---", order.additional_notes, ""] : []),
    ...(order.pronunciation_url ? [`Pronunciation Recording: ${order.pronunciation_url}`] : []),
  ];

  const blob = new Blob([lines.join("\n")], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `order-${order.order_number}-survey.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

function ImageThumbnail({ img }: { img: Tables<"image_uploads"> }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    supabase.storage.from("customer-photos").createSignedUrl(img.file_path, 3600).then(({ data }) => {
      if (data?.signedUrl) setUrl(data.signedUrl);
    });
  }, [img.file_path]);

  const handleDownload = async () => {
    // Use signed URL + window.open for mobile compatibility
    const { data } = await supabase.storage
      .from("customer-photos")
      .createSignedUrl(img.file_path, 3600, { download: img.file_name });
    if (data?.signedUrl) {
      window.open(data.signedUrl, "_blank");
    }
  };

  return (
    <div className="relative group aspect-square rounded-lg overflow-hidden bg-muted">
      {url ? (
        <img src={url} alt={img.file_name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      )}
      <button
        onClick={handleDownload}
        className="absolute bottom-1 right-1 bg-background/80 backdrop-blur-sm rounded-md p-1.5 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity hover:bg-background"
        title={`Download ${img.file_name}`}
      >
        <Download className="w-3.5 h-3.5 text-foreground" />
      </button>
    </div>
  );
}

const AdminOrderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [order, setOrder] = useState<SongOrder | null>(null);
  const [customerName, setCustomerName] = useState<string | null>(null);
  const [customerEmail, setCustomerEmail] = useState<string | null>(null);
  const [pronunciationUrl, setPronunciationUrl] = useState<string | null>(null);
  const [images, setImages] = useState<Tables<"image_uploads">[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [updating, setUpdating] = useState(false);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoLoop, setVideoLoop] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoErrorRetried = useRef(false);

  useEffect(() => {
    if (!authLoading && !adminLoading) {
      if (!user) navigate("/login");
      else if (!isAdmin) navigate("/dashboard");
    }
  }, [user, isAdmin, authLoading, adminLoading, navigate]);

  useEffect(() => {
    const fetchAll = async () => {
      if (!id || !isAdmin) return;
      const [orderRes, imgRes] = await Promise.all([
        supabase.from("song_orders").select("*").eq("id", id).maybeSingle(),
        supabase.from("image_uploads").select("*").eq("order_id", id),
      ]);
      if (orderRes.data) {
        setOrder(orderRes.data);
        // Fetch customer profile name and email
        const [profileRes, adminEmailRes] = await Promise.all([
          supabase.from("profiles").select("full_name").eq("user_id", orderRes.data.user_id).maybeSingle(),
          supabase.functions.invoke("get-admin-emails", { body: { user_id: orderRes.data.user_id } }),
        ]);
        if (profileRes.data?.full_name) setCustomerName(profileRes.data.full_name);
        if (adminEmailRes.data?.email) setCustomerEmail(adminEmailRes.data.email);
        // Generate signed URLs for deliverables and pronunciation
        const urls: Record<string, string> = {};
        const fieldsToSign = [
          { key: "pronunciation_url", value: orderRes.data.pronunciation_url },
          { key: "final_audio_url", value: orderRes.data.final_audio_url },
          { key: "final_video_url", value: orderRes.data.final_video_url },
          { key: "lyrics_pdf_url", value: orderRes.data.lyrics_pdf_url },
        ];
        for (const field of fieldsToSign) {
          if (!field.value) continue;

          const normalizedPath = normalizeCustomerPhotosPath(field.value);
          if (normalizedPath) {
            const { data: signedData } = await supabase.storage
              .from("customer-photos")
              .createSignedUrl(normalizedPath, 3600);
            if (signedData?.signedUrl) urls[field.key] = signedData.signedUrl;
            continue;
          }

          if (field.value.startsWith("http")) {
            urls[field.key] = field.value;
          }
        }
        setSignedUrls(urls);
        if (urls.pronunciation_url) setPronunciationUrl(urls.pronunciation_url);
      }
      if (imgRes.data) setImages(imgRes.data);
      setLoading(false);
    };
    if (isAdmin) fetchAll();
  }, [id, isAdmin]);

  const refreshSignedDeliverableUrl = async (key: string, rawValue: string | null | undefined) => {
    if (!rawValue) return;
    const normalizedPath = normalizeCustomerPhotosPath(rawValue);
    if (!normalizedPath) return;

    const { data: signedData, error } = await supabase.storage
      .from("customer-photos")
      .createSignedUrl(normalizedPath, 3600);

    if (error) {
      console.error(`Failed to refresh signed URL for ${key}:`, error.message);
      return;
    }

    if (signedData?.signedUrl) {
      setSignedUrls((prev) => ({ ...prev, [key]: signedData.signedUrl }));
    }
  };

  const updateStatus = async (status: OrderStatus) => {
    if (!order) return;
    setUpdating(true);
    const { error } = await supabase.from("song_orders").update({ status }).eq("id", order.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setOrder({ ...order, status });
      toast({ title: "Status updated" });

      // Send email notification to customer
      supabase.functions.invoke("send-order-notification", {
        body: { order_id: order.id, type: "status_change", new_status: status },
      }).then(({ error: fnError }) => {
        if (fnError) console.error("Notification error:", fnError);
      });
    }
    setUpdating(false);
  };

  const deleteOrder = async () => {
    if (!order) return;
    setUpdating(true);

    // Collect storage files to delete
    const filesToDelete: string[] = [];
    if (order.pronunciation_url) filesToDelete.push(order.pronunciation_url);
    if (order.final_audio_url) filesToDelete.push(order.final_audio_url);
    if (order.final_video_url) filesToDelete.push(order.final_video_url);
    if (order.lyrics_pdf_url) filesToDelete.push(order.lyrics_pdf_url);
    if (order.draft_audio_url) filesToDelete.push(order.draft_audio_url);
    if (order.draft_video_url) filesToDelete.push(order.draft_video_url);

    // Get image file paths before deleting records
    const imagePaths = images.map(img => img.file_path);
    filesToDelete.push(...imagePaths);

    // Delete storage files
    if (filesToDelete.length > 0) {
      await supabase.storage.from("customer-photos").remove(filesToDelete);
    }

    // Delete related data first, then the order
    await Promise.all([
      supabase.from("order_messages").delete().eq("order_id", order.id),
      
      supabase.from("image_uploads").delete().eq("order_id", order.id),
      supabase.from("reviews").delete().eq("order_id", order.id),
    ]);
    const { error } = await supabase.from("song_orders").delete().eq("id", order.id);
    if (error) {
      toast({ title: "Error deleting order", description: error.message, variant: "destructive" });
      setUpdating(false);
    } else {
      toast({ title: "Order deleted" });
      navigate("/admin");
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>, type: "audio" | "video" | "pdf") => {
    const file = e.target.files?.[0];
    // Reset the input so the same file can be re-selected
    e.target.value = "";
    if (!file || !order) return;
    setUpdating(true);

    const path = `deliverables/${order.id}/${type}-${Date.now()}-${file.name}`;
    const { error: uploadError } = await supabase.storage
      .from("customer-photos")
      .upload(path, file);

    if (uploadError) {
      toast({ title: "Upload failed", description: uploadError.message, variant: "destructive" });
      setUpdating(false);
      return;
    }

    const col = type === "audio" ? "final_audio_url" : type === "video" ? "final_video_url" : "lyrics_pdf_url";
    const updateData: Record<string, any> = { [col]: path };
    if (type === "audio" && order.status !== "ready_for_review" && order.status !== "approved" && order.status !== "completed") {
      updateData.status = "ready_for_review";
    }
    
    const { error } = await supabase.from("song_orders").update(updateData).eq("id", order.id);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      const updatedOrder = { ...order, ...updateData };
      setOrder(updatedOrder as any);
      const { data: signedData } = await supabase.storage
        .from("customer-photos")
        .createSignedUrl(path, 3600);
      if (signedData?.signedUrl) {
        setSignedUrls(prev => ({ ...prev, [col]: signedData.signedUrl }));
      }
      const labels = { audio: "Audio", video: "Video", pdf: "PDF" };
      toast({ title: `${labels[type]} uploaded${updateData.status ? " — Status changed to Ready for Review" : ""}` });
      
      if (updateData.status) {
        supabase.functions.invoke("send-order-notification", {
          body: { order_id: order.id, type: "status_change", new_status: "ready_for_review" },
        });
      }
    }
    setUpdating(false);
  };

  const downloadAllImages = async () => {
    if (images.length === 0) return;
    toast({ title: "Preparing ZIP...", description: `Packaging ${images.length} image(s).` });
    const zip = new JSZip();
    const usedNames = new Map<string, number>();
    for (const img of images) {
      const { data } = await supabase.storage.from("customer-photos").download(img.file_path);
      if (data) {
        let name = img.file_name;
        const count = usedNames.get(name) || 0;
        if (count > 0) {
          const dotIdx = name.lastIndexOf(".");
          const base = dotIdx > 0 ? name.slice(0, dotIdx) : name;
          const ext = dotIdx > 0 ? name.slice(dotIdx) : "";
          name = `${base}_${count}${ext}`;
        }
        usedNames.set(img.file_name, count + 1);
        zip.file(name, data);
      }
    }
    const blob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `order-${order?.order_number}-photos.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    toast({ title: "Download ready", description: `${images.length} image(s) zipped.` });
  };

  if (authLoading || adminLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-16 container mx-auto px-4 text-center">
          <p className="text-muted-foreground">Order not found.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate("/admin")}>Back to Admin</Button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <Button variant="ghost" className="mb-6" onClick={() => navigate("/admin")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
          </Button>

          <div className="space-y-6">
            {/* Order Header + Status */}
            <div className="bg-card rounded-2xl shadow-soft p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                  <h1 className="font-heading text-2xl font-bold text-foreground">
                    {eventTypeLabels[order.event_type] || order.event_type} Song
                  </h1>
                  {(customerName || customerEmail) && (
                    <p className="text-sm text-foreground font-medium mt-1">
                      <User className="w-3.5 h-3.5 inline mr-1" />
                      {customerName || "Unknown"}{customerEmail ? ` · ${customerEmail}` : ""}
                    </p>
                  )}
                  <p className="text-muted-foreground text-sm">
                    Order #{order.order_number} · {order.plan_type.charAt(0).toUpperCase() + order.plan_type.slice(1)} Plan
                    {order.price_paid ? ` · $${Number(order.price_paid).toFixed(2)}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={order.status} onValueChange={(v) => updateStatus(v as OrderStatus)} disabled={updating}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-9 w-9">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => exportSurvey(order, customerName, customerEmail)}>
                        <Download className="w-4 h-4 mr-2" /> Export Survey
                      </DropdownMenuItem>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive focus:text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete Order
                          </DropdownMenuItem>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this order?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete order #{order.order_number} and all related data (messages, images, reviews). This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => setShowDeleteConfirm(true)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                              Yes, delete this order
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <AlertDialog open={showDeleteConfirm} onOpenChange={(open) => { setShowDeleteConfirm(open); if (!open) setDeleteConfirmText(""); }}>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Type <strong>DELETE</strong> below to confirm permanent deletion of order #{order.order_number}.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <Input
                        placeholder="Type DELETE to confirm"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        className="my-2"
                      />
                      <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setDeleteConfirmText("")}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={deleteOrder}
                          disabled={deleteConfirmText !== "DELETE"}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
                        >
                          Delete permanently
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <Detail label="Recipient" value={order.recipient_names} />
                <Detail label="Relationship" value={order.relationship_to_recipient || "—"} />
                <Detail label="Event Date" value={order.event_date ? new Date(order.event_date).toLocaleDateString() : "—"} />
                <Detail label="Style" value={order.musical_style} />
                <Detail label="Mood" value={order.mood} />
                <Detail label="Tempo" value={order.tempo || "Medium"} />
                <Detail label="Language" value={order.language || "English"} />
                <Detail label="Rush Delivery" value={order.rush_delivery ? "Yes" : "No"} />
                <Detail label="Song Approved" value={order.song_approved ? "✅ Yes" : "No"} />
                <Detail label="Video Approved" value={order.video_approved ? "✅ Yes" : "No"} />
              </div>

              {/* Pronunciation Recording */}
              {pronunciationUrl && (
                <div className="mt-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Name Pronunciation</p>
                  <audio controls src={pronunciationUrl} className="w-full" />
                </div>
              )}
              {/* Story */}
              <div className="mt-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Story & Memories</p>
                <p className="text-sm text-foreground bg-muted/50 rounded-lg p-3">{order.memories}</p>
              </div>
              {order.specific_phrases && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Specific Phrases</p>
                  <p className="text-sm text-foreground bg-muted/50 rounded-lg p-3">{order.specific_phrases}</p>
                </div>
              )}
              {order.additional_notes && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Additional Notes</p>
                  <p className="text-sm text-foreground bg-muted/50 rounded-lg p-3">{order.additional_notes}</p>
                </div>
              )}

              {/* Customer Photos */}
              <div className="mt-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Customer Photos {images.length > 0 ? `(${images.length})` : ""}
                </p>
                {order.plan_type === "premium" || order.plan_type === "premium_plus" ? (
                  images.length > 0 ? (
                    <>
                      <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-3 flex items-center justify-between">
                        <p className="text-sm font-medium text-foreground">📸 Premium order — download all images for the video</p>
                        <Button size="sm" onClick={downloadAllImages}>
                          <Download className="w-4 h-4 mr-2" /> Download All Photos
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                        {images.map((img) => (
                          <ImageThumbnail key={img.id} img={img} />
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
                      📸 Premium order — no images uploaded yet by the customer.
                    </p>
                  )
                ) : (
                  <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
                    Photos not included in the Essentials package.
                  </p>
                )}
              </div>
            </div>

            {/* Deliverables */}
            <div className="bg-card rounded-2xl shadow-soft p-6">
              <h2 className="font-heading text-lg font-semibold text-foreground mb-4">Deliverables</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-foreground">Audio File</p>
                  </div>
                  {order.final_audio_url ? (
                    <div className="space-y-2">
                      <audio controls src={signedUrls.final_audio_url || order.final_audio_url} className="w-full" />
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-primary flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Uploaded</p>
                        <label className={`relative inline-flex items-center justify-center text-xs h-8 px-3 rounded-md hover:bg-accent hover:text-accent-foreground cursor-pointer ${updating ? "pointer-events-none opacity-50" : ""}`}>
                          Replace
                          <input type="file" accept=".mp3,.wav,.m4a,.aac,.flac,.ogg,.wma,audio/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={(e) => handleFileSelected(e, "audio")} />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <label className={`relative inline-flex items-center justify-center gap-2 text-sm h-9 px-4 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground cursor-pointer ${updating ? "pointer-events-none opacity-50" : ""}`}>
                      <Upload className="w-4 h-4" /> Upload Audio
                      <input type="file" accept=".mp3,.wav,.m4a,.aac,.flac,.ogg,.wma,audio/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={(e) => handleFileSelected(e, "audio")} />
                    </label>
                  )}
                </div>

                <div className="border border-border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-foreground">Video File</p>
                  </div>
                    {order.final_video_url ? (
                      <div className="space-y-2">
                        <video
                          ref={videoRef}
                          playsInline
                          preload="auto"
                          loop={videoLoop}
                          className="w-full rounded max-h-64"
                          onPlay={() => setVideoPlaying(true)}
                          onPause={() => setVideoPlaying(false)}
                          onEnded={() => setVideoPlaying(false)}
                          onTimeUpdate={() => {
                            if (videoRef.current && videoRef.current.duration) {
                              setVideoProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
                            }
                          }}
                          onError={() => {
                            if (videoErrorRetried.current) return;
                            videoErrorRetried.current = true;
                            void refreshSignedDeliverableUrl("final_video_url", order.final_video_url);
                          }}
                          src={signedUrls.final_video_url || order.final_video_url}
                        >
                          Your browser does not support video playback.
                        </video>
                        {/* Custom controls */}
                        <div className="mt-2 space-y-2">
                          <div
                            className="cursor-pointer"
                            onClick={(e) => {
                              if (!videoRef.current || !videoRef.current.duration) return;
                              const rect = e.currentTarget.getBoundingClientRect();
                              const pct = (e.clientX - rect.left) / rect.width;
                              videoRef.current.currentTime = pct * videoRef.current.duration;
                            }}
                          >
                            <Progress value={videoProgress} className="h-2 cursor-pointer" />
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary/20 transition-colors"
                              onClick={() => {
                                if (!videoRef.current) return;
                                videoRef.current.paused ? videoRef.current.play() : videoRef.current.pause();
                              }}
                              aria-label={videoPlaying ? "Pause video" : "Play video"}
                            >
                              {videoPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors ml-auto"
                              onClick={async () => {
                                videoErrorRetried.current = false;
                                await refreshSignedDeliverableUrl("final_video_url", order.final_video_url);
                                if (videoRef.current) videoRef.current.load();
                              }}
                            >
                              <RefreshCw className="w-3 h-3" /> Refresh video
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-primary flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Uploaded</p>
                          <label className={`relative inline-flex items-center justify-center text-xs h-8 px-3 rounded-md hover:bg-accent hover:text-accent-foreground cursor-pointer ${updating ? "pointer-events-none opacity-50" : ""}`}>
                            Replace
                            <input type="file" accept=".mp4,.mov,.avi,.mkv,.webm,video/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={(e) => handleFileSelected(e, "video")} />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <label className={`relative inline-flex items-center justify-center gap-2 text-sm h-9 px-4 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground cursor-pointer ${updating ? "pointer-events-none opacity-50" : ""}`}>
                        <Upload className="w-4 h-4" /> Upload Video
                        <input type="file" accept=".mp4,.mov,.avi,.mkv,.webm,video/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={(e) => handleFileSelected(e, "video")} />
                      </label>
                    )}
                  </div>

                {/* PDF / Lyrics Document */}
                <div className="border border-border rounded-xl p-4">
                  <p className="text-sm font-medium text-foreground mb-2">PDF / Lyrics</p>
                  {(order as any).lyrics_pdf_url ? (
                    <div className="space-y-2">
                      <a href={signedUrls.lyrics_pdf_url || "#"} target="_blank" rel="noopener noreferrer"
                        className={`text-sm text-primary hover:underline flex items-center gap-1 ${!signedUrls.lyrics_pdf_url ? "pointer-events-none opacity-50" : ""}`}>
                        <FileText className="w-4 h-4" /> {signedUrls.lyrics_pdf_url ? "View PDF" : "Loading..."}
                      </a>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-primary flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Uploaded</p>
                        <label className={`relative inline-flex items-center justify-center text-xs h-8 px-3 rounded-md hover:bg-accent hover:text-accent-foreground cursor-pointer ${updating ? "pointer-events-none opacity-50" : ""}`}>
                          Replace
                          <input type="file" accept=".pdf,application/pdf" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={(e) => handleFileSelected(e, "pdf")} />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <label className={`relative inline-flex items-center justify-center gap-2 text-sm h-9 px-4 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground cursor-pointer ${updating ? "pointer-events-none opacity-50" : ""}`}>
                      <Upload className="w-4 h-4" /> Upload PDF
                      <input type="file" accept=".pdf,application/pdf" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={(e) => handleFileSelected(e, "pdf")} />
                    </label>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

const Detail = ({ label, value }: { label: string; value: string }) => (
  <div>
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className="text-foreground">{value}</p>
  </div>
);

export default AdminOrderDetail;
